import {
  Box,
  Button,
  Card,
  Container,
  Flex,
  Heading,
  Spinner,
  Stack,
  Text,
  ThemeProvider,
} from '@sanity/ui'
import {useToast} from '@sanity/ui/toast'
import {RefreshIcon} from '@sanity/icons/Refresh'
import {useState} from 'react'

import {buildTheme} from '@sanity/ui/theme'

const DashboardConfig: React.FC = () => {
  const theme = buildTheme()
  const basePath = window.location.origin
  const toast = useToast()

  const [isAlgoliaLoading, setIsAlgoliaLoading] = useState(false)
  const [isNextjsCacheLoading, setIsNextjsCacheLoading] = useState(false)

  const handleTriggerAlgoliaSync = async () => {
    setIsAlgoliaLoading(true)
    try {
      await forceAlgoliaFullSync(basePath)
      toast.push({
        status: 'success',
        title: 'Algolia sync started successfully',
      })
    } catch (error) {
      console.error('Error triggering Algolia sync:', error)
      toast.push({
        status: 'error',
        title: 'Failed to trigger Algolia sync',
        description: error instanceof Error ? error.message : 'Unknown error',
      })
    } finally {
      setIsAlgoliaLoading(false)
    }
  }

  const handleTriggerNextjsCacheInvalidation = async () => {
    setIsNextjsCacheLoading(true)
    try {
      await forceNextjsCacheInvalidation(basePath)
      toast.push({
        status: 'success',
        title: 'Next.js cache invalidation started successfully',
      })
    } catch (error) {
      console.error('Error triggering Next.js cache invalidation:', error)
      toast.push({
        status: 'error',
        title: 'Failed to trigger cache invalidation',
        description: error instanceof Error ? error.message : 'Unknown error',
      })
    } finally {
      setIsNextjsCacheLoading(false)
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <Container width={2}>
        {/* Intro text */}
        <Box padding={4} marginTop={5}>
          <Flex
            gap={4}
            align="flex-start"
            direction={['column', 'column', 'row']}
            justify={['flex-start', 'flex-start', 'space-between']}
          >
            <Stack gap={4} style={{flex: 1, minWidth: 0}}>
              <Heading as="h2" size={3}>
                Force cache invalidation
              </Heading>
              <Text size={2} style={{maxWidth: '70ch'}}>
                Use this in case the content on the website is not up-to-date.
              </Text>
            </Stack>
          </Flex>

          <Stack gap={4} marginTop={[5, 5, 6]}>
            <Card padding={3} radius={2} shadow={1}>
              <Flex
                align="center"
                direction={['column', 'column', 'row']}
                justify={['flex-start', 'flex-start', 'space-between']}
              >
                <Stack gap={1}>
                  <Heading as="h3" size={1} style={{marginBottom: '0.5em'}}>
                    Algolia (search)
                  </Heading>
                </Stack>

                <Box marginTop={[3, 3, 0]}>
                  <Button
                    tone="positive"
                    mode="ghost"
                    width="fill"
                    onClick={handleTriggerAlgoliaSync}
                    disabled={isAlgoliaLoading}
                    icon={isAlgoliaLoading ? Spinner : RefreshIcon}
                    text={isAlgoliaLoading ? 'Syncing...' : 'Force sync'}
                  ></Button>
                </Box>
              </Flex>
            </Card>
            <Card padding={3} radius={2} shadow={1}>
              <Flex
                align="center"
                direction={['column', 'column', 'row']}
                justify={['flex-start', 'flex-start', 'space-between']}
              >
                <Stack gap={1}>
                  <Heading as="h3" size={1} style={{marginBottom: '0.5em'}}>
                    Website (content)
                  </Heading>
                </Stack>

                <Box marginTop={[3, 3, 0]}>
                  <Button
                    tone="positive"
                    mode="ghost"
                    width="fill"
                    onClick={handleTriggerNextjsCacheInvalidation}
                    disabled={isNextjsCacheLoading}
                    icon={isNextjsCacheLoading ? Spinner : RefreshIcon}
                    text={isNextjsCacheLoading ? 'Invalidating...' : 'Invalidate cache'}
                  ></Button>
                </Box>
              </Flex>
            </Card>
          </Stack>
        </Box>
      </Container>
    </ThemeProvider>
  )
}

export default DashboardConfig

const forceAlgoliaFullSync = async (basePath: string) => {
  return invokeAPI(basePath, 'algolia-full-sync', {})
}

const forceNextjsCacheInvalidation = async (basePath: string) => {
  return invokeAPI(basePath, 'cache-invalidate', {all: true})
}

const getSanityAuthToken = (): string | null => {
  const raw = localStorage.getItem('__studio_auth_token_rd0y3pad')
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw)
    return parsed.token ?? parsed
  } catch {
    return raw
  }
}

const invokeAPI = async (basePath: string, apiName: string, body: {}) => {
  const token = getSanityAuthToken()
  if (!token) {
    throw new Error('Not authenticated with Sanity Studio')
  }

  const requestUrl = new URL(`${basePath}/.netlify/functions/${apiName}`)
  const response = await fetch(requestUrl.toString(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  return true
}
